// Translations. Every feature module ships its own messages file and registers
// it when imported, so a page's strings live next to the page:
//
//   import { registerMessages, t } from '$lib/i18n.svelte';
//   registerMessages({ nl: { 'timer.start': 'Start' }, en: { 'timer.start': 'Start' } });
//   t('timer.start');  t('entries.count', { count: 3 });
//
// Placeholders are {name}. Plurals: give 'key' and 'key.one'; 'key.one' is used
// when vars.count === 1. Missing keys fall back to English, then Dutch, then the
// key itself.

export type Lang = 'nl' | 'en' | 'de' | 'fr';
export type Messages = Partial<Record<Lang, Record<string, string>>>;

export const LANGUAGES: Array<{ code: Lang; label: string }> = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' }
];

const dictionaries: Record<Lang, Record<string, string>> = { nl: {}, en: {}, de: {}, fr: {} };

export function registerMessages(messages: Messages): void {
  for (const lang of Object.keys(messages) as Lang[]) {
    Object.assign(dictionaries[lang], messages[lang]);
  }
}

function detect(): Lang {
  try {
    const stored = JSON.parse(localStorage.getItem('tp.prefs') || '{}').language;
    if (stored && stored in dictionaries) return stored as Lang;
  } catch { /* ignore */ }
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'nl').slice(0, 2).toLowerCase();
  return (['nl', 'en', 'de', 'fr'].includes(nav) ? nav : 'nl') as Lang;
}

class I18n {
  lang = $state<Lang>(typeof window === 'undefined' ? 'nl' : detect());

  set(lang: Lang) {
    this.lang = lang;
    if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', lang);
  }

  get locale(): string {
    return { nl: 'nl-NL', en: 'en-GB', de: 'de-DE', fr: 'fr-FR' }[this.lang];
  }
}

export const i18n = new I18n();

function lookup(key: string, lang: Lang): string | undefined {
  return dictionaries[lang][key] ?? (lang !== 'en' ? dictionaries.en[key] : undefined) ?? dictionaries.nl[key];
}

export function t(key: string, vars?: Record<string, string | number | null | undefined>): string {
  const lang = i18n.lang; // reactive dependency
  let message = vars && vars.count === 1 ? lookup(`${key}.one`, lang) ?? lookup(key, lang) : lookup(key, lang);
  if (message === undefined) return key;
  if (vars) {
    message = message.replace(/\{(\w+)\}/g, (_, name: string) => {
      const value = vars[name];
      return value === undefined || value === null ? '' : String(value);
    });
  }
  return message;
}

/** True when a key exists (for optional hints). */
export function has(key: string): boolean {
  return lookup(key, i18n.lang) !== undefined;
}
